import React, {
  useState,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useHistory,
} from 'react-router-dom';
import i18n from 'i18next';

import {
  coreActions,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  uiActions,
} from 'web-actions';

import {
  Header,
  AddEntity,
} from 'web-components';

const mapState = state => ({
  ...getUiState([
    'namespace',
    'namespaceModal',
  ])(state),
});

type Props = {
  profile: any;
};

const MainHeader = ({ profile }: Props) => {
  const {
    namespace,
    namespaceModal,
  } = useSelector(mapState, shallowEqual);
  const isPro = profile.is_pro;
  const history = useHistory();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');

  const onKeyDown = (e) => {
    if (
      e.key === 'Enter'
      && searchQuery
    ) {
      const { pathname } = window.location;
      if (pathname.startsWith('/search')) {
        history.push(`/search/${searchQuery}/${pathname.split('/')[3]}`);
      } else {
        history.push(`/search/${searchQuery}/all`);
      }
    }
  };

  const profileActions = [
    {
      icon: 'individuals',
      name: 'Profile',
      action: () => history.push('/users/me/edit'),
    },
    {
      icon: 'clients',
      name: i18n.format(i18n.t('client_plural'), 'cap'),
      action: () => history.push('/clients/individuals'),
      isHidden: !isPro,
    },
    {
      icon: 'payment',
      name: 'Payments',
      action: () => history.push('/payments/details'),
      isHidden: !isPro || namespace.symbol === 'ideafit',
    },
    {
      icon: 'payment',
      name: 'My Account',
      action: () => history.push('/account/details'),
      isHidden: isPro || ['gymcloud', 'ideafit'].indexOf(namespace.symbol) > -1,
    },
    {
      icon: 'properties',
      name: 'Set namespace',
      action: () => dispatch(
        uiActions.setUiState({
          namespaceModal: true,
        }),
      ),
      isHidden: process.env.TARGET_ENV === 'production',
    },
    {
      icon: 'signout',
      name: 'Sign out',
      action: () => dispatch(
        coreActions.logoutRequest(),
      ),
      isHidden: namespace.symbol === 'ideafit' && isPro,
    },
  ].filter(a => !a.isHidden);

  const addActions = [
    {
      icon: 'client',
      name: i18n.t('add.client'),
      action: () => dispatch(
        uiActions.setUiState({
          addClientModal: true,
        }),
      ),
    },
    {
      icon: 'exercise',
      name: i18n.t('add.exercises'),
      action: () => dispatch(
        uiActions.setUiState({
          addEntityModal: {
            isOpen: true,
            folderName: 'Exercises',
            resourceType: 'exercises',
          },
        }),
      ),
    },
    {
      icon: 'workout',
      name: i18n.t('add.workoutTemplates'),
      action: () => dispatch(
        uiActions.setUiState({
          addEntityModal: {
            isOpen: true,
            folderName: 'Workout Templates',
            resourceType: 'workoutTemplates',
          },
        }),
      ),
    },
    {
      icon: 'program',
      name: i18n.t('add.programTemplates'),
      action: () => dispatch(
        uiActions.setUiState({
          addEntityModal: {
            isOpen: true,
            folderName: 'Program Templates',
            resourceType: 'programTemplates',
          },
        }),
      ),
    },
    {
      icon: 'video',
      name: i18n.t('add.video'),
      action: () => dispatch(
        uiActions.setUiState({
          uploadVideoModal: {
            multiple: false,
            isOpen: true,
          },
        }),
      ),
      isHidden: namespace.symbol === 'ideafit',
    },
    {
      icon: 'announcement',
      name: i18n.t('add.announcement'),
      action: () => {
        history.push('/new_announcement');
      },
      isHidden: namespace.symbol === 'ideafit',
    },
  ].filter(a => !a.isHidden);

  return (
    <>
      <Header
        isPro={isPro}
        avatar={profile.user_profile?.avatar?.thumb.url}
        addActions={addActions}
        profileActions={profileActions}
        namespace={namespace}
        onKeyDown={onKeyDown}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      <AddEntity
        label="Set namespace"
        isOpen={namespaceModal}
        buttonLabel="Set"
        onRequestClose={() => {
          dispatch(uiActions.setUiState({ namespaceModal: false }));
        }}
        onSubmit={(values, callbacks) => {
          window.localStorage.setItem('namespace', values.name);
          setTimeout(() => {
            callbacks.setSubmitting(false);
            window.location.reload();
          }, 600);
        }}
      />
    </>
  );
};

export default MainHeader;
